import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { WhosOnline } from '../room/types';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { timeSince } from '@par/shared/utils/date-time';
import { BiGhost } from 'react-icons/bi';
import { resizeImage } from '../../api/cloudinary';
import { PlayerActivityStatus } from '../bottom-nav/PlayerActivityStatus';
import { getAvatarSrc } from '../room/utils';

export const MAX_PLAYERS_ONLINE_COUNT = 50;

type PropTypes = {
  handleGhostChallenge: (player: WhosOnline) => void;
  handleGhostChallengesView: () => void;
  incomingGhostChallenges?: number;
  isAdmin: boolean;
  playersOnlineCount?: number;
  profileId: string;
  whosOnline?: WhosOnline[];
};

// DEPRECATE THIS COMPONENT (testingFeature)

export const WhosOnlineCard = ({
  handleGhostChallenge,
  handleGhostChallengesView,
  incomingGhostChallenges,
  isAdmin,
  playersOnlineCount,
  profileId,
  whosOnline,
}: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePlayerClick = (profileId: string) => {
    navigate(`/profile/overview/${profileId}`);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleGhostChallengeClick = (player: WhosOnline) => {
    if (player.ghostChallengeStatus === 'no') return;
    handleGhostChallenge(player);
  };

  const ghostChallengeStatusTooltip = (status: 'yes' | 'no' | 'maybe') => {
    let message = 'May or may not respond to challenges';
    if (status === 'yes') {
      message = 'Send me challenges!';
    }
    if (status === 'no') {
      message = 'Not accepting challenges right now';
    }
    return <Typography variant="caption">Status: {message}</Typography>;
  };

  const sortUsers = (whosOnline?: WhosOnline[]) => {
    if (!whosOnline) return [];
    const onlineUsers = whosOnline
      .filter((u) => u.online && !u.isIdle)
      .sort((a, b) => b.loggedInAt - a.loggedInAt);
    const idleUsers = whosOnline
      .filter((u) => u.online && u.isIdle)
      .sort((a, b) => b.loggedInAt - a.loggedInAt);
    const offlineUsers = whosOnline
      .filter((u) => !u.online)
      .sort((a, b) => b.disconnectedAt - a.disconnectedAt);
    return onlineUsers.concat(idleUsers).concat(offlineUsers);
  };

  return (
    <>
      {/* <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1 }}>I use Popover.</Typography>
      </Popover> */}

      <List>
        {whosOnline
          ?.filter((p) => p.profileId === profileId)
          .map((player, index) => (
            <ListItem key={player.profileId} disablePadding disableGutters>
              <ListItemButton
                onClick={() => handlePlayerClick(player?.profileId)}
                key={index}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
              >
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    badgeContent={
                      player.isMobile ? (
                        <PhoneAndroidIcon
                          sx={{
                            background: theme.palette.secondary.main,
                            padding: '3px',
                            borderRadius: '16px',
                          }}
                          fontSize="small"
                        />
                      ) : null
                    }
                  >
                    <Avatar
                      alt={player?.username}
                      src={getAvatarSrc(
                        player.avatarPublicId,
                        player.avatarUrl,
                        100,
                        100
                      )}
                      sx={{
                        filter: player.online ? '' : 'grayscale(100%)',
                      }}
                    />
                  </Badge>
                </ListItemAvatar>
                <Stack>
                  <ListItemText primary={player?.username} />
                  <PlayerActivityStatus player={player} />
                </Stack>
              </ListItemButton>
              <Tooltip title={'View my Ghost Challenges'} placement="top">
                <IconButton
                  onClick={handleGhostChallengesView}
                  sx={{
                    position: 'absolute',
                    right: '2%',
                  }}
                >
                  <Badge badgeContent={incomingGhostChallenges} color="warning">
                    <BiGhost
                      style={{
                        color:
                          player.ghostChallengeStatus === 'yes'
                            ? theme.palette.underPar.main
                            : player.ghostChallengeStatus === 'no'
                            ? 'gray'
                            : theme.palette.primary.main,
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
      </List>
      <Divider />
      {playersOnlineCount > MAX_PLAYERS_ONLINE_COUNT &&
      profileId !== '63211bde4d491da0fe4c290d' ? (
        <Stack
          spacing={2}
          sx={{ display: 'flex', justifyContent: 'center' }}
          mt={4}
          paddingX={2}
        >
          <Typography
            variant="h4"
            textAlign={'center'}
            fontWeight={600}
            color="success.main"
          >
            {playersOnlineCount}
          </Typography>
          <Typography textAlign={'center'}>players online</Typography>
          <Typography variant="caption" textAlign={'center'} paddingTop={20}>
            Note: the full list of players is not available when the player
            count is greater than 50. This is a temporary limitation while we
            build for scale.
          </Typography>
        </Stack>
      ) : (
        <List
          sx={{
            minHeight: '300px',
            // maxHeight: '500px',
            overflow: 'auto',
          }}
        >
          {sortUsers(whosOnline)
            .filter((p) => p.profileId !== profileId)
            .slice(0, 80)
            .map((player, index) => (
              <Box key={player.profileId}>
                <ListItem disablePadding disableGutters>
                  <ListItemButton
                    onClick={() => handlePlayerClick(player?.profileId)}
                    key={index}
                    onMouseEnter={handlePopoverOpen}
                    onMouseLeave={handlePopoverClose}
                    sx={{
                      '& .ghost-challenge-button': {
                        display: 'none',
                      },
                      '&:hover .ghost-challenge-button': {
                        display: 'flex',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        badgeContent={
                          player.isMobile ? (
                            <PhoneAndroidIcon
                              sx={{
                                background: theme.palette.secondary.main,
                                padding: '3px',
                                borderRadius: '16px',
                              }}
                              fontSize="small"
                            />
                          ) : null
                        }
                      >
                        <Avatar
                          alt={player?.username}
                          src={getAvatarSrc(
                            player.avatarPublicId,
                            player.avatarUrl,
                            100,
                            100
                          )}
                          sx={{
                            filter: player.online ? '' : 'grayscale(100%)',
                          }}
                        />
                      </Badge>
                    </ListItemAvatar>
                    <Stack>
                      <ListItemText primary={player?.username} />
                      {player.online && player.isIdle ? (
                        <Typography variant="caption" color="info.main">
                          Idle
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingSolo ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Solo
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingMatch ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Match
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingSnG ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Sit & Go
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingTeams ? (
                        <Typography variant="caption" color="success.main">
                          Playing Teams
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingDaily ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Daily
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingStarterKit ? (
                        <Typography variant="caption" color="success.main">
                          Playing {player.isPlayingStarterKit}
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingGhostChallenge ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Ghost Challenge
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isSeedingGame ? (
                        <Typography variant="caption" color="success.main">
                          Seeding a game
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingSearchedGame ? (
                        <Typography variant="caption" color="success.main">
                          Playing a Searched Match
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingDailyTower ? (
                        <Typography variant="caption" color="success.main">
                          Playing the Daily Tower
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingSurvivalEndurance ? (
                        <Typography variant="caption" color="success.main">
                          Playing an Endurance Survival
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingSurvivalChallenge ? (
                        <Typography variant="caption" color="success.main">
                          Playing the Challenge Tower
                        </Typography>
                      ) : player.online &&
                        !player.isIdle &&
                        player.isPlayingDemons ? (
                        <Typography variant="caption" color="success.main">
                          Facing demons
                        </Typography>
                      ) : player.online && !player.isIdle ? (
                        <Typography variant="caption" color="success.main">
                          Online
                        </Typography>
                      ) : (
                        <Typography variant="caption">
                          Last online:{' '}
                          {timeSince(new Date(player.disconnectedAt))} ago
                        </Typography>
                      )}
                    </Stack>
                  </ListItemButton>
                  <Tooltip
                    title={ghostChallengeStatusTooltip(
                      player.ghostChallengeStatus
                    )}
                    placement="top"
                  >
                    <IconButton
                      onClick={() => handleGhostChallengeClick(player)}
                      sx={{
                        position: 'absolute',
                        right: '2%',
                      }}
                    >
                      <BiGhost
                        style={{
                          color:
                            player.ghostChallengeStatus === 'yes'
                              ? theme.palette.underPar.main
                              : player.ghostChallengeStatus === 'no'
                              ? 'gray'
                              : theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                <Divider variant="inset" component="li" />
              </Box>
            ))}
        </List>
      )}
    </>
  );
};
